import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Select,
  Input,
  HStack,
  VStack,
  Spinner,
  Text,
  Badge,
  FormControl,
  FormLabel,
  TableContainer,
  Icon,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFindMany, useUser } from "@gadgetinc/react";
import { api } from "../api";
import { FiEye, FiChevronUp, FiChevronDown } from "react-icons/fi";

const EVALUATION_SELECT = {
  id: true,
  evaluationDate: true,
  overallScore: true,
  status: true,
  comments: true,
  criticalFailures: true,
  completionTime: true,
  student: {
    id: true,
    firstName: true,
    lastName: true,
    email: true,
  },
  scenario: {
    id: true,
    title: true,
  },
  cohort: {
    id: true,
    name: true,
    displayName: true,
  },
  shareToken: {
    id: true,
  },
  simulationSession: {
    id: true,
    evaluationForm: true,
  },
};

const TEMPLATE_LABELS = {
  ph4456: "PH4456",
  nremt_medical: "NREMT Medical",
  nremt_trauma: "NREMT Trauma",
};

const normalizeEvaluation = (evaluation, type) => ({
  ...evaluation,
  evaluationType: type,
  templateLabel: TEMPLATE_LABELS[type] || type,
  sourceLabel: evaluation.shareToken?.id ? "Shared Link" : "Direct",
});

export default function MyEvaluations() {
  const user = useUser(api);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("sessionId") || "";

  const [selectedCohort, setSelectedCohort] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState("evaluationDate");
  const [sortDirection, setSortDirection] = useState("Descending");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filter = useMemo(() => {
    if (!user?.id) return null;

    const conditions = [
      {
        scenario: {
          createdBy: {
            id: { equals: user.id },
          },
        },
      },
    ];

    if (sessionId) {
      conditions.push({
        simulationSession: {
          id: { equals: sessionId },
        },
      });
    }

    if (selectedCohort) {
      conditions.push({
        cohort: {
          id: { equals: selectedCohort },
        },
      });
    }

    if (selectedStatus) {
      conditions.push({
        status: { equals: selectedStatus },
      });
    }

    if (selectedSource === "direct") {
      conditions.push({
        shareToken: { isSet: false },
      });
    } else if (selectedSource === "shared") {
      conditions.push({
        shareToken: { isSet: true },
      });
    }

    if (startDate) {
      conditions.push({
        evaluationDate: { greaterThanOrEqual: new Date(startDate).toISOString() },
      });
    }

    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      conditions.push({
        evaluationDate: { lessThanOrEqual: endOfDay.toISOString() },
      });
    }

    if (debouncedSearch) {
      conditions.push({
        OR: [
          { student: { firstName: { matches: debouncedSearch } } },
          { student: { lastName: { matches: debouncedSearch } } },
          { student: { email: { matches: debouncedSearch } } },
        ],
      });
    }

    return { AND: conditions };
  }, [user?.id, sessionId, selectedCohort, selectedStatus, selectedSource, startDate, endDate, debouncedSearch]);

  const queryOptions = useMemo(() => ({
    filter,
    sort: { evaluationDate: "Descending" },
    select: EVALUATION_SELECT,
    first: 250,
  }), [filter]);

  const [{ data: ph4456Evaluations, fetching: fetchingPh4456, error: ph4456Error }] = useFindMany(
    api.ph4456Evaluation,
    queryOptions,
    { pause: !filter }
  );

  const [{ data: nremtMedicalEvaluations, fetching: fetchingNremtMedical, error: nremtMedicalError }] = useFindMany(
    api.nremtMedicalEvaluation,
    queryOptions,
    { pause: !filter }
  );

  const [{ data: nremtTraumaEvaluations, fetching: fetchingNremtTrauma, error: nremtTraumaError }] = useFindMany(
    api.nremtTraumaEvaluation,
    queryOptions,
    { pause: !filter }
  );

  const [{ data: cohorts }] = useFindMany(api.cohort, {
    select: {
      id: true,
      name: true,
      displayName: true,
    },
    first: 250,
  });

  const allEvaluations = useMemo(() => {
    return [
      ...(ph4456Evaluations || []).map((evaluation) => normalizeEvaluation(evaluation, "ph4456")),
      ...(nremtMedicalEvaluations || []).map((evaluation) => normalizeEvaluation(evaluation, "nremt_medical")),
      ...(nremtTraumaEvaluations || []).map((evaluation) => normalizeEvaluation(evaluation, "nremt_trauma")),
    ];
  }, [ph4456Evaluations, nremtMedicalEvaluations, nremtTraumaEvaluations]);

  const filteredEvaluations = useMemo(() => {
    const templateFiltered = selectedTemplate
      ? allEvaluations.filter((evaluation) => evaluation.evaluationType === selectedTemplate)
      : allEvaluations;

    const valueForSort = (evaluation) => {
      switch (sortField) {
        case "student":
          return `${evaluation.student?.lastName || ""} ${evaluation.student?.firstName || ""}`.trim().toLowerCase();
        case "scenario":
          return (evaluation.scenario?.title || "").toLowerCase();
        case "template":
          return (evaluation.templateLabel || "").toLowerCase();
        case "status":
          return (evaluation.status || "").toLowerCase();
        case "overallScore":
          return Number(evaluation.overallScore ?? -1);
        case "evaluationDate":
        default:
          return new Date(evaluation.evaluationDate || 0).getTime();
      }
    };

    return [...templateFiltered].sort((a, b) => {
      const aValue = valueForSort(a);
      const bValue = valueForSort(b);

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "Ascending" ? aValue - bValue : bValue - aValue;
      }

      const compare = String(aValue).localeCompare(String(bValue));
      return sortDirection === "Ascending" ? compare : -compare;
    });
  }, [allEvaluations, selectedTemplate, sortField, sortDirection]);

  const isFetching = fetchingPh4456 || fetchingNremtMedical || fetchingNremtTrauma;
  const fetchError = ph4456Error || nremtMedicalError || nremtTraumaError;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "Ascending" ? "Descending" : "Ascending");
    } else {
      setSortField(field);
      setSortDirection(field === "evaluationDate" ? "Descending" : "Ascending");
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "Ascending" ? <Icon as={FiChevronUp} /> : <Icon as={FiChevronDown} />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "yellow";
      case "completed":
        return "green";
      case "reviewed":
        return "blue";
      default:
        return "gray";
    }
  };

  if (!user) {
    return (
      <Container maxW="container.xl" py={8}>
        <Spinner />
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        <HStack spacing={4} align="center" justify="space-between" wrap="wrap">
          <HStack spacing={4} align="center">
            <Button
              as={Link}
              to="/my-scenarios"
              leftIcon={<ArrowBackIcon />}
              variant="ghost"
              colorScheme="blue"
            >
              Back to My Scenarios
            </Button>
            <Heading size="lg">My Scenario Evaluations</Heading>
          </HStack>
          {sessionId && (
            <Badge colorScheme="purple" fontSize="sm" px={3} py={1}>
              Filtered to session {sessionId}
            </Badge>
          )}
        </HStack>

        <Box bg="gray.800" p={4} borderRadius="md" shadow="sm">
          <VStack spacing={4} align="stretch">
            <HStack spacing={4} wrap="wrap">
              <FormControl maxW="220px">
                <FormLabel fontSize="sm">Cohort</FormLabel>
                <Select
                  placeholder="All Cohorts"
                  value={selectedCohort}
                  onChange={(e) => setSelectedCohort(e.target.value)}
                  size="sm"
                >
                  {cohorts?.map((cohort) => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.displayName || cohort.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl maxW="180px">
                <FormLabel fontSize="sm">Template</FormLabel>
                <Select
                  placeholder="All Templates"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  size="sm"
                >
                  <option value="ph4456">PH4456</option>
                  <option value="nremt_medical">NREMT Medical</option>
                  <option value="nremt_trauma">NREMT Trauma</option>
                </Select>
              </FormControl>

              <FormControl maxW="180px">
                <FormLabel fontSize="sm">Status</FormLabel>
                <Select
                  placeholder="All Statuses"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  size="sm"
                >
                  <option value="draft">Draft</option>
                  <option value="completed">Completed</option>
                  <option value="reviewed">Reviewed</option>
                </Select>
              </FormControl>

              <FormControl maxW="180px">
                <FormLabel fontSize="sm">Source</FormLabel>
                <Select
                  placeholder="All Sources"
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  size="sm"
                >
                  <option value="direct">Direct</option>
                  <option value="shared">Shared Link</option>
                </Select>
              </FormControl>

              <FormControl maxW="180px">
                <FormLabel fontSize="sm">Start Date</FormLabel>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} size="sm" />
              </FormControl>

              <FormControl maxW="180px">
                <FormLabel fontSize="sm">End Date</FormLabel>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} size="sm" />
              </FormControl>
            </HStack>

            <HStack spacing={4} wrap="wrap" align="end">
              <FormControl maxW="420px">
                <FormLabel fontSize="sm">Search Student</FormLabel>
                <Input
                  placeholder="Search by student name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="sm"
                />
              </FormControl>

              {(selectedCohort || selectedTemplate || selectedStatus || selectedSource || startDate || endDate || searchQuery) && (
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  onClick={() => {
                    setSelectedCohort("");
                    setSelectedTemplate("");
                    setSelectedStatus("");
                    setSelectedSource("");
                    setStartDate("");
                    setEndDate("");
                    setSearchQuery("");
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </HStack>
          </VStack>
        </Box>

        <Box bg="gray.800" borderRadius="md" shadow="sm" overflowX="auto">
          {isFetching ? (
            <Box p={8} textAlign="center">
              <Spinner size="lg" />
              <Text mt={4} color="white">Loading evaluations...</Text>
            </Box>
          ) : fetchError ? (
            <Box p={8} textAlign="center">
              <Text color="red.500">Error loading evaluations: {fetchError.message}</Text>
            </Box>
          ) : filteredEvaluations.length === 0 ? (
            <Box p={8} textAlign="center">
              <Text color="gray.400" fontSize="lg">
                No evaluations found. Try adjusting your filters.
              </Text>
            </Box>
          ) : (
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg="gray.700">
                  <Tr>
                    <Th cursor="pointer" onClick={() => handleSort("student")}>
                      <HStack spacing={1}>
                        <Text>Student Name</Text>
                        {renderSortIcon("student")}
                      </HStack>
                    </Th>
                    <Th>Student Email</Th>
                    <Th cursor="pointer" onClick={() => handleSort("scenario")}>
                      <HStack spacing={1}>
                        <Text>Scenario</Text>
                        {renderSortIcon("scenario")}
                      </HStack>
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort("template")}>
                      <HStack spacing={1}>
                        <Text>Template</Text>
                        {renderSortIcon("template")}
                      </HStack>
                    </Th>
                    <Th>Cohort</Th>
                    <Th cursor="pointer" onClick={() => handleSort("evaluationDate")}>
                      <HStack spacing={1}>
                        <Text>Evaluation Date</Text>
                        {renderSortIcon("evaluationDate")}
                      </HStack>
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort("overallScore")}>
                      <HStack spacing={1}>
                        <Text>Score</Text>
                        {renderSortIcon("overallScore")}
                      </HStack>
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort("status")}>
                      <HStack spacing={1}>
                        <Text>Status</Text>
                        {renderSortIcon("status")}
                      </HStack>
                    </Th>
                    <Th>Source</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredEvaluations.map((evaluation, index) => (
                    <Tr
                      key={`${evaluation.evaluationType}-${evaluation.id}`}
                      bg={index % 2 === 0 ? "gray.800" : "gray.700"}
                      _hover={{ bg: "gray.600" }}
                      transition="background 0.2s"
                    >
                      <Td>
                        {evaluation.student?.firstName} {evaluation.student?.lastName}
                      </Td>
                      <Td>{evaluation.student?.email || "N/A"}</Td>
                      <Td>{evaluation.scenario?.title || "N/A"}</Td>
                      <Td>
                        <Badge colorScheme="cyan">{evaluation.templateLabel}</Badge>
                      </Td>
                      <Td>{evaluation.cohort?.displayName || evaluation.cohort?.name || "N/A"}</Td>
                      <Td>{formatDate(evaluation.evaluationDate)}</Td>
                      <Td fontWeight="bold">{evaluation.overallScore ?? "N/A"}</Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(evaluation.status)}>{evaluation.status || "unknown"}</Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={evaluation.shareToken?.id ? "purple" : "green"}>
                          {evaluation.sourceLabel}
                        </Badge>
                      </Td>
                      <Td>
                        <Button
                          size="xs"
                          colorScheme="blue"
                          leftIcon={<FiEye />}
                          onClick={() => navigate(`/evaluation/${evaluation.id}?type=${encodeURIComponent(evaluation.evaluationType)}`)}
                        >
                          Open Review
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </VStack>
    </Container>
  );
}
